import type { FC, PropsWithChildren } from 'react';
import { createContext, memo, useContext, useEffect, useMemo, useState } from 'react';

import { useConferenceStore } from 'store/conference';
import { attachMicrophoneMessageReceivedEvent } from 'store/conference/events/attachMicrophoneMessageReceivedEvent';
import { attachVOXEndpointUpdateEvent } from 'store/conference/events/attachVOXEndpointUpdateEvent';
import { useCurrentUserStore } from 'store/currentUser';
import type { IUser } from 'store/declare';
import { useSizeStore } from 'store/size';

interface IContext {
    /** Обновить список пользователей */
    setUsers: React.Dispatch<React.SetStateAction<IContext['users']>>;
    /** Список пользователей */
    users: IUser[];
}

const Context = createContext<IContext>({
    setUsers: () => undefined,
    users: [],
});

/** Хранилище, связанное с пользователями */
export const useUsersStore = () => useContext(Context);

const UsersStore: FC<PropsWithChildren> = ({ children }) => {
    const [list, setList] = useState<IContext['users']>([]);
    const { VOXCallInstance } = useConferenceStore();
    const { currentUser } = useCurrentUserStore();
    const { setIsFull } = useSizeStore();

    const contextValue = useMemo<IContext>(() => {
        return {
            setUsers: setList,
            userScreenShare: list.find(user => user.isSharing) ?? null,
            users: list,
        };
    }, [list]);

    useEffect(() => {
        if (VOXCallInstance) {
            attachVOXEndpointUpdateEvent({
                currentUserId: currentUser?.id,
                updateSize: setIsFull,
                updateUserList: setList,
                voxCall: VOXCallInstance,
            });
            attachMicrophoneMessageReceivedEvent(VOXCallInstance, setList);
        }
    }, [VOXCallInstance, currentUser?.id]);

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default memo(UsersStore);
